import React, { useEffect, useId, useRef, useState } from 'react';
import { Truck, X } from 'lucide-react';
import { FaFacebookF, FaInstagram } from 'react-icons/fa6';
import { ASSETS } from '../constants/assets';
import { useTranslations } from '../translations';
import IllegalContentNoticeModal from './IllegalContentNoticeModal';
import { isAnalyticsConfigured } from '../services/analytics';
import { openCookiePreferences } from '../services/analyticsConsent';
import { requestAppInstall, useCanInstallApp } from '../services/appInstall';
import { appLinkClick, createPageUrl } from '../utils/appRoutes';
import './Footer.css';

interface FooterProps {
  onNavigateToTerms?: () => void;
  onNavigateToPrivacy?: () => void;
  onNavigateToCookies?: () => void;
  onNavigateToAbout?: () => void;
  onNavigateToContact?: () => void;
}

const Footer: React.FC<FooterProps> = ({
  onNavigateToTerms,
  onNavigateToPrivacy,
  onNavigateToCookies,
  onNavigateToAbout,
  onNavigateToContact,
}) => {
  const t = useTranslations();
  const canInstallApp = useCanInstallApp();
  const [showTransportNotice, setShowTransportNotice] = useState(false);
  const [showIllegalContentNotice, setShowIllegalContentNotice] = useState(false);
  const transportNoticeTitleId = useId();
  const transportNoticeDescriptionId = useId();
  const closeTransportNoticeRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    if (!showTransportNotice) return;

    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    closeTransportNoticeRef.current?.focus();

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setShowTransportNotice(false);
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.body.style.overflow = previousOverflow;
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [showTransportNotice]);

  const handleNavigateToTerms = () => {
    setShowTransportNotice(false);
    onNavigateToTerms?.();
  };

  return (
    <>
      <footer className="footer">
      <div className="footer-content">
        <div className="footer-brand">
          <div className="footer-logo">
            <img src={ASSETS.LOGO} alt="roade.ro" className="footer-logo-image" />
          </div>
          <p className="footer-brand-description">{t.footer.description}</p>
          <div className="social-links">
            <a href="https://www.facebook.com/people/roadero/61595613294606" aria-label="Facebook" target="_blank" rel="noopener noreferrer">
              <FaFacebookF className="social-icon" aria-hidden="true" />
            </a>
            <a href="https://www.instagram.com/roade.ro" aria-label="Instagram" target="_blank" rel="noopener noreferrer">
              <FaInstagram className="social-icon" aria-hidden="true" />
            </a>
          </div>
          {canInstallApp && (
            <button type="button" className="footer-install-app" onClick={requestAppInstall}>
              <img src="/icons/icon-192.png?v=15" alt="" className="footer-install-app-icon" />
              <span className="footer-install-app-text">
                <small>{t.footer.installAppHint}</small>
                <strong>{t.footer.installApp}</strong>
              </span>
            </button>
          )}
        </div>

        <nav className="footer-links" aria-label={t.footer.navigationLabel}>
          <div className="footer-column">
            <h3 className="footer-column-title">{t.footer.company}</h3>
            <ul className="footer-menu">
              <li>
                <a
                  className="footer-link"
                  href={createPageUrl('about')}
                  onClick={appLinkClick(onNavigateToAbout)}
                >
                  {t.footer.about}
                </a>
              </li>
              <li>
                <a
                  className="footer-link"
                  href={createPageUrl('contact')}
                  onClick={appLinkClick(onNavigateToContact)}
                >
                  {t.footer.contact}
                </a>
              </li>
            </ul>
          </div>

          <div className="footer-column">
            <h3 className="footer-column-title">{t.footer.legal}</h3>
            <ul className="footer-menu">
              <li>
                <a
                  className="footer-link"
                  href={createPageUrl('terms')}
                  onClick={appLinkClick(onNavigateToTerms)}
                >
                  {t.footer.termsAndConditions}
                </a>
              </li>
              <li>
                <a
                  className="footer-link"
                  href={createPageUrl('privacy')}
                  onClick={appLinkClick(onNavigateToPrivacy)}
                >
                  {t.footer.privacyPolicy}
                </a>
              </li>
              <li>
                <a
                  className="footer-link"
                  href={createPageUrl('cookies')}
                  onClick={appLinkClick(onNavigateToCookies)}
                >
                  {t.footer.cookiesPolicy}
                </a>
              </li>
              {isAnalyticsConfigured() && (
                <li>
                  <button
                    type="button"
                    className="footer-link"
                    onClick={openCookiePreferences}
                    aria-haspopup="dialog"
                  >
                    {t.cookieConsent.preferences}
                  </button>
                </li>
              )}
              <li>
                <button
                  type="button"
                  className="footer-link"
                  onClick={() => setShowIllegalContentNotice(true)}
                  aria-haspopup="dialog"
                >
                  {t.footer.reportIllegalContent}
                </button>
              </li>
              <li>
                <button
                  type="button"
                  className="footer-link"
                  onClick={() => setShowTransportNotice(true)}
                  aria-haspopup="dialog"
                >
                  {t.footer.transport}
                </button>
              </li>
            </ul>
          </div>
        </nav>

        <div className="footer-anpc">
          <span className="footer-anpc-label">{t.footer.consumerProtection}</span>
          <a
            href="https://anpc.ro/ce-este-sal/"
            target="_blank"
            rel="noopener noreferrer"
            aria-label="ANPC SAL"
          >
            <img src={ASSETS.ANPC_SAL} alt="ANPC SAL" className="anpc-logo" />
          </a>
        </div>
      </div>

      <div className="footer-bottom">
        <div className="footer-bottom-content">
          <p className="copyright">{t.footer.copyright}</p>
          <p className="copyright">{t.footer.operator}</p>
        </div>
      </div>
      </footer>

      {showTransportNotice && (
        <div className="footer-transport-overlay" onMouseDown={() => setShowTransportNotice(false)}>
          <section
            className="footer-transport-dialog"
            role="dialog"
            aria-modal="true"
            aria-labelledby={transportNoticeTitleId}
            aria-describedby={transportNoticeDescriptionId}
            onMouseDown={(event) => event.stopPropagation()}
          >
            <button
              ref={closeTransportNoticeRef}
              type="button"
              className="footer-transport-close"
              onClick={() => setShowTransportNotice(false)}
              aria-label={t.footer.transportDialogClose}
            >
              <X size={20} aria-hidden="true" />
            </button>

            <div className="footer-transport-icon" aria-hidden="true">
              <Truck size={24} />
            </div>
            <h2 id={transportNoticeTitleId} className="footer-transport-title">
              {t.footer.transportDialogTitle}
            </h2>
            <p id={transportNoticeDescriptionId} className="footer-transport-description">
              {t.footer.transportDialogDescription}
            </p>
            <p className="footer-transport-policy">{t.footer.transportDialogPolicy}</p>

            <div className="footer-transport-actions">
              <button
                type="button"
                className="footer-transport-button footer-transport-button-secondary"
                onClick={() => setShowTransportNotice(false)}
              >
                {t.footer.transportDialogClose}
              </button>
              <button
                type="button"
                className="footer-transport-button footer-transport-button-primary"
                onClick={handleNavigateToTerms}
              >
                {t.footer.transportDialogTerms}
              </button>
            </div>
          </section>
        </div>
      )}
      {showIllegalContentNotice && (
        <IllegalContentNoticeModal
          onClose={() => setShowIllegalContentNotice(false)}
          onNavigateToPrivacy={onNavigateToPrivacy}
        />
      )}
    </>
  );
};

export default Footer;
